import { has, isEmpty, omit, pick } from 'lodash';
import { AuthMethodRoute } from '../constants/routes';
import {
	IConnectors,
	CustomAuthConnector,
	ConnectorTypeEnum,
	ConnectorIndexEnum,
	ProviderPrefix,
	IExtendedForm,
	SyntheticConnectorProps,
	IOnConnectorSelectProps,
	IDefaultConnectorProps,
	CUSTOM_TEMPLATES_PROP,
	DEFAULT_CONNECTOR_ID,
	IProviderCollection,
	ProviderPropTypes,
	ConnectorKeys,
	ITestServicePayloadProps,
	TestMessagePropTypes,
	TestMessageTargetTypes,
	EMAIL_TEST_PROPS,
	SMS_TEST_PROPS,
	ConnectorProtocolKeys,
	ITestEmailServiceValues,
	ITestSmsServiceValues,
	IResetMethodTemplates
} from '../types/connectors';
import { IOneTimeProviderSettings } from '../types/settings';

/**
 * Add default `Descope` provider for display level connector lists
 * Default connector is a minimal set of props, and should not be saved on the server
 * This is implemented in order to keep the default off the actual data list only in display level
 * @param connectors
 * @returns
 */
const defaultConnector = (type: string) =>
	({
		common: {
			id: DEFAULT_CONNECTOR_ID,
			displayName: DEFAULT_CONNECTOR_ID
		},
		providerType: type
	} as CustomAuthConnector);

const getTargetProp = (providerType: ProviderPrefix) => {
	switch (providerType) {
		case ProviderPrefix.EMAIL:
			return TestMessageTargetTypes.TO_EMAIL;
		case ProviderPrefix.SMS:
			return TestMessageTargetTypes.TO_PHONE;
		default:
			return '';
	}
};

const getSettingsProp = (isAdhoc: boolean) =>
	isAdhoc ? TestMessagePropTypes.ADHOC : TestMessagePropTypes.SERVICE;

const getPayloadVals = (providerType: ProviderPrefix, values: any) => {
	if (providerType === ProviderPrefix.EMAIL) {
		if (values.emailServiceProvider !== DEFAULT_CONNECTOR_ID) {
			return {
				notificationTextTitle: values.customEmailSubject,
				notificationTextHTML: values.customEmailBody
			};
		}
		return {
			notificationTextTitle: values.emailSubject,
			notificationTextHTML: values.emailBody
		};
	}

	if (providerType === ProviderPrefix.SMS) {
		if (values.textServiceProvider !== DEFAULT_CONNECTOR_ID) {
			return {
				notificationTextBody: values.customTextMessageBody
			};
		}
		return { notificationTextBody: values.textMessageBody };
	}

	return {};
};

const getAdhocPayloadVals = (
	values: ITestEmailServiceValues | ITestSmsServiceValues | CustomAuthConnector
) => {
	const removedProps = [
		'common',
		'apiKeyChanged',
		'apiSecretChanged',
		'authChanged'
	].concat(SyntheticConnectorProps);
	return omit(values, removedProps);
};

export const isEmptyProviders = (sample: IProviderCollection) =>
	isEmpty(omit(sample, [CUSTOM_TEMPLATES_PROP]));

/**
 * Utility function to flatten a nested object of connectors from the same type.
 * Each connector will be updated with `type` (e.g. email/sms) and `schema` (e.g. sengrid/smtp)
 * @param connectors
 * @param type
 * @returns
 */
export const flattenConnectors = (
	connectors: Record<ConnectorIndexEnum, CustomAuthConnector[]>,
	type: ConnectorTypeEnum
) => {
	const connectorIds = Object.keys(omit(connectors, 'customTemplates'));

	if (isEmpty(connectorIds)) {
		return [];
	}

	return connectorIds
		.map((schema) =>
			connectors[schema as ConnectorIndexEnum].map((connector) => ({
				...connector,
				providerType: type,
				providerProtocol: schema,
				providerDisplayName: connector.common.displayName
			}))
		)
		.reduce((acc, curr) => acc.concat(curr));
};

/**
 * Serialize provider object:
 *
 * {
 *   provider1: {
 *      typeA: [
 *          {connectorA1},
 *          {connectorA2}
 *      ],
 *      typeB: [
 *          {connectorB1},
 *          {connectorB2}
 *   ]
 *  },
 *  provider2: {
 *      typeC: [
 *          {connectorC1},
 *          {connectorC2}
 *      ]
 *   }
 * }
 *
 * will transform into: { p1: [cA1, cA2, cB1, cB2...], p2: [cC1, cC2] }
 *
 * @param providers
 * @param loading
 * @returns
 */
export const getConnectorConfigs = (
	providers: IConnectors | undefined
): Partial<Record<ConnectorTypeEnum, CustomAuthConnector[]>> => {
	if (!providers) {
		return { emailProviders: [], smsProviders: [] };
	}

	const { emailProviders, smsProviders } = providers;

	const connectors = {
		emailProviders: omit(emailProviders, [CUSTOM_TEMPLATES_PROP]),
		smsProviders: omit(smsProviders, [CUSTOM_TEMPLATES_PROP])
	} as Record<ConnectorTypeEnum, any>;

	const keys = Object.keys(connectors) as ConnectorTypeEnum[];

	return Object.fromEntries(
		keys.map((providerType) => [
			providerType,
			flattenConnectors(connectors[providerType], providerType)
		])
	);
};

export const withDefaultConnector = (
	type: ProviderPrefix,
	connectors: CustomAuthConnector[] | undefined
): CustomAuthConnector[] => [defaultConnector(type)].concat(connectors || []);

export const getServiceId = (protocol: string, id: string) =>
	!protocol || !id ? DEFAULT_CONNECTOR_ID : `${protocol}:${id}`;

export const getConnectorProtocol = (type: string) =>
	type.split(':')[0] as ConnectorProtocolKeys;

export const getSelectedConnectorId = (providerId: string) => {
	const split = providerId.split(':');

	if (!providerId.includes(':') || split.some((s) => !s.length)) {
		return DEFAULT_CONNECTOR_ID;
	}
	return !providerId || providerId === DEFAULT_CONNECTOR_ID
		? DEFAULT_CONNECTOR_ID
		: split[1];
};

export const getDisplayFieldId = ({
	fieldName,
	customFieldName,
	provider
}: {
	fieldName: string;
	customFieldName: string;
	provider: string;
}) => {
	if (provider === DEFAULT_CONNECTOR_ID) {
		return fieldName;
	}
	return customFieldName;
};

export const getCustomSmsTemplateProps = (
	vals: IExtendedForm<any>
): Partial<IExtendedForm<any>> => {
	const { textMessageBody, customTextMessageBody } = vals;
	const isDefaultSmsProvider =
		vals.textServiceProvider === DEFAULT_CONNECTOR_ID;
	const selectedTextMessageBody = isDefaultSmsProvider
		? textMessageBody
		: customTextMessageBody;
	return {
		textMessageBody: selectedTextMessageBody
	};
};

/**
 * Context Utils
 */
export const getCommonProps = (connector: CustomAuthConnector) => {
	const { common, providerDisplayName } = connector;
	return {
		id: common?.id,
		displayName: providerDisplayName
	};
};

export const updateConnectorItem = (
	connectors: any,
	connector: CustomAuthConnector,
	type: string,
	protocol: string
) => {
	const sample = connectors[type][protocol].map((c: CustomAuthConnector) => {
		if (c.common.id === connector.common.id) {
			const payload = omit(
				{
					...connector,
					...{ common: getCommonProps(connector) }
				},
				SyntheticConnectorProps
			);
			return payload;
		}
		return c;
	});
	return sample;
};

export const removeConnectorItem = (
	connector: CustomAuthConnector,
	connectors: IConnectors,
	defaults: IDefaultConnectorProps,
	authPage: AuthMethodRoute
) => {
	const sample = connectors;
	const { providerType: type, providerProtocol: protocol, common } = connector;
	const { id } = common;

	const collection = sample[type!][protocol!].filter(
		({ common: c }: CustomAuthConnector) => c.id !== id
	);

	if (isEmpty(collection)) {
		(sample as any)[type] = omit(sample[type], [protocol]);
	} else {
		sample[type][protocol] = collection;
	}

	// If no custom connectors reset custom template to default
	if (
		type === ConnectorTypeEnum.EMAIL_PROVIDERS &&
		isEmptyProviders(sample.emailProviders)
	) {
		sample.emailProviders.customTemplates[authPage] = {
			emailSubject: defaults.emailSubject,
			emailBody: defaults.emailBody
		} as any;
	}
	if (
		type === ConnectorTypeEnum.SMS_PROVIDERS &&
		isEmptyProviders(sample.smsProviders)
	) {
		sample.smsProviders.customTemplates[authPage] = {
			textMessageBody: defaults.textBody
		} as any;
	}

	return sample;
};

export const getUpdatedConnectors = (
	connector: CustomAuthConnector,
	connectors: IConnectors
) => {
	const sample = connectors;
	const {
		providerType: type,
		providerProtocol: protocol,
		providerDisplayName: displayName
	} = connector;

	if (!has(sample[type], protocol)) {
		sample[type][protocol] = [];
	}

	// add/edit connector in collection
	if (!has(connector, 'common.id')) {
		sample[type][protocol].push({
			...omit(connector, SyntheticConnectorProps),
			...{ common: { displayName } }
		} as CustomAuthConnector);
	} else {
		sample[type][protocol] = updateConnectorItem(
			connectors,
			connector,
			type!,
			protocol!
		);
	}

	return sample;
};

export const onConnectorSelect =
	(props: IOnConnectorSelectProps) => (field: string, id: string) => {
		const {
			keys,
			form,
			defaultTemplates,
			emailCustomTemplates,
			smsCustomTemplates,
			resetMethod
		} = props;

		const {
			emailServiceProvider,
			textServiceProvider,
			emailBody,
			emailSubject,
			customEmailSubject,
			customEmailBody,
			textMessageBody
		} = keys;

		const { setFieldValue } = form;

		if (id === DEFAULT_CONNECTOR_ID) {
			if (field === emailServiceProvider) {
				setFieldValue(field, DEFAULT_CONNECTOR_ID);
				if (resetMethod) {
					const resetMethodTpls = defaultTemplates as IResetMethodTemplates;
					setFieldValue(
						emailBody,
						resetMethodTpls[resetMethod as any].emailBody
					);
					setFieldValue(
						emailSubject,
						resetMethodTpls[resetMethod as any]?.emailSubject
					);
				} else {
					setFieldValue(
						emailBody,
						(defaultTemplates as IDefaultConnectorProps)?.emailBody
					);
					setFieldValue(
						emailSubject,
						(defaultTemplates as IDefaultConnectorProps)?.emailSubject
					);
				}
			}

			if (field === textServiceProvider) {
				setFieldValue(
					textMessageBody,
					(defaultTemplates as IDefaultConnectorProps)?.textBody
				);
			}
		} else {
			setFieldValue(field, id);
			if (field === emailServiceProvider) {
				if (resetMethod) {
					const customEmailResetTpl = emailCustomTemplates;
					setFieldValue(customEmailSubject, customEmailResetTpl?.emailSubject);
					setFieldValue(customEmailBody, customEmailResetTpl?.emailBody);
				} else {
					setFieldValue(customEmailSubject, emailCustomTemplates?.emailSubject);
					setFieldValue(emailBody, emailCustomTemplates?.emailBody);
				}
			}

			if (field === textServiceProvider) {
				setFieldValue(
					textMessageBody,
					smsCustomTemplates?.textMessageBody || ''
				);
			}
		}
	};

export const getServiceProviderKey = (type: ConnectorKeys) => {
	switch (type) {
		case ConnectorTypeEnum.EMAIL_PROVIDERS:
			return ProviderPropTypes.EMAIL_PROVIDER;
		case ConnectorTypeEnum.SMS_PROVIDERS:
			return ProviderPropTypes.TEXT_PROVIDER;
		default:
			return '';
	}
};

export const getCustomTemplateVals = (
	vals: IExtendedForm<IOneTimeProviderSettings>
) => {
	const isDefaultEmailProvider =
		vals.emailServiceProvider === DEFAULT_CONNECTOR_ID;

	const isDefaultSmsProvider =
		vals.textServiceProvider === DEFAULT_CONNECTOR_ID;

	const emailBody = isDefaultEmailProvider
		? vals.emailBody
		: vals.customEmailBody;

	const emailSubject = isDefaultEmailProvider
		? vals.emailSubject
		: vals.customEmailSubject;

	const textMessageBody = isDefaultSmsProvider
		? vals.textMessageBody
		: vals.customTextMessageBody;

	return { ...vals, emailBody, emailSubject, textMessageBody };
};

export const getTestServicePayload = ({
	isAdhoc,
	target,
	values,
	pageId,
	providerType,
	providerProtocol,
	providerId
}: ITestServicePayloadProps) => {
	const settings = isAdhoc
		? getAdhocPayloadVals(values)
		: getPayloadVals(providerType, values);

	const props = {
		MessageType: providerType,
		ProviderType: providerProtocol,
		AuthenticationMethod: pageId,
		[getSettingsProp(isAdhoc)]: settings,
		[getTargetProp(providerType)]: target
	};

	const payload = providerId ? { Provider: providerId, ...props } : props;

	return payload;
};

export const getServiceTypeValues = (type: ProviderPrefix, values: any) => {
	switch (type) {
		case ProviderPrefix.EMAIL:
			return pick(values, EMAIL_TEST_PROPS) as ITestEmailServiceValues;
		case ProviderPrefix.SMS:
			return pick(values, SMS_TEST_PROPS) as ITestSmsServiceValues;
		default:
			return values;
	}
};

export const isCustomProvider = (
	connectorId: string,
	connectors: CustomAuthConnector[]
) => {
	if (connectorId === DEFAULT_CONNECTOR_ID) {
		return false;
	}

	if (!connectors) {
		return false;
	}

	return (
		connectors.filter((c: CustomAuthConnector) => c.common.id === connectorId)
			.length === 1
	);
};
